import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";

interface DNCEntry {
  id: string | number;
  email?: string;
  phone?: string;
  reason: string;
  addedAt: string | Date;
  addedBy?: string;
}

interface DNCTableProps {
  entries: DNCEntry[];
  searchFilter: string;
  onSearchFilterChange: (value: string) => void;
  onRemove: (entry: DNCEntry) => void;
  contactType: 'email' | 'sms';
  isLoading?: boolean;
  searchPlaceholder?: string;
}

export function DNCTable({
  entries,
  searchFilter,
  onSearchFilterChange,
  onRemove,
  contactType,
  isLoading = false,
  searchPlaceholder,
}: DNCTableProps) {
  const defaultPlaceholder = contactType === 'email' 
    ? "Search DNC list..." 
    : "Search DNC list...";
  
  const contactColumnHeader = contactType === 'email' ? 'Email' : 'Phone';
  const getContactValue = (entry: DNCEntry) => contactType === 'email' ? entry.email : entry.phone;

  const filteredEntries = entries.filter(entry => {
    const contactValue = getContactValue(entry);
    if (!contactValue) return false;
    return contactValue.toLowerCase().includes(searchFilter.toLowerCase()) ||
           entry.reason?.toLowerCase().includes(searchFilter.toLowerCase());
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>DNC List</CardTitle>
        <CardDescription>
          {contactType === 'email' 
            ? 'Emails that are blocked from receiving campaigns'
            : 'Phone numbers that are blocked from receiving SMS campaigns'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center space-x-2 mb-4">
          <Input
            placeholder={searchPlaceholder || defaultPlaceholder}
            value={searchFilter}
            onChange={(e) => onSearchFilterChange(e.target.value)}
            className="flex-1"
          />
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{contactColumnHeader}</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Added Date</TableHead>
              <TableHead>Added By</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredEntries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                  No DNC entries found
                </TableCell>
              </TableRow>
            ) : (
              filteredEntries.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell className="font-medium">{getContactValue(entry)}</TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {entry.reason?.replace('_', ' ')}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {new Date(entry.addedAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>{entry.addedBy || 'System'}</TableCell>
                  <TableCell>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onRemove(entry)}
                      disabled={isLoading}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}


